import React, { useState, useEffect, useCallback, useRef } from "react";
import { ButtonOutline, Heading } from "../../../components/styledConstants";
import { Box, CircularProgress } from "@mui/material";
import Filter from "./Components/Filter";
import CardProductTable from "./Components/CardProductTable";
import AddCardProductModal from "./Components/AddCardProductModal";
import { addCardProductRequest, listCardProductsRequest, markCardProductDefaultRequest } from "../../../utils/api";
import { toast } from "react-toastify";

const CardProductManagement = () => {
  const [tableLoading, setTableLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalSubmitting, setModalSubmitting] = useState(false);
  const [cardProducts, setCardProducts] = useState([]);
  const [pagination, setPagination] = useState({
    pageNo: 0,
    pageSize: 10,
    totalElements: 0,
    totalPages: 0,
  });
  const hasInitialized = useRef(false);

  const fetchCardProducts = useCallback(
    async (payload = { cardProductName: null, bin: null, productId: null, pageNo: 0, pageSize: 10 }) => {
      setTableLoading(true);
      try {
        const apiPayload = { ...payload, pageNo: Number(payload.pageNo) || 0 };

        const res = await listCardProductsRequest(apiPayload);
        const responsePayload = res?.data ?? res;

        if (responsePayload?.code === "0000" && responsePayload?.data) {
          setCardProducts(responsePayload.data.content || []);
          setPagination({
            pageNo: responsePayload.data.number,
            pageSize: apiPayload.pageSize,
            totalElements: responsePayload.data.totalElements,
            totalPages: responsePayload.data.totalPages,
          });
        } else {
          console.error("Failed to fetch card products:", responsePayload);
          toast.error("Failed to load card products.", {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        }
      } catch (error) {
        console.error("Fetch card products error:", error);
        toast.error("Network error while fetching products.", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } finally {
        setTableLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      fetchCardProducts();
    }
  }, []);

  const handleAddCardProduct = () => {
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleModalSubmit = async (data) => {
    setModalSubmitting(true);
    try {
      const res = await addCardProductRequest(data);
      const payload = res?.data ?? res;

      if (payload?.code === "0000") {
        const toastMessage = payload?.message || payload?.msg || "Card product created successfully.";
        toast.success(toastMessage, {
          icon: false,
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        setModalOpen(false);
        fetchCardProducts();
      } else {
        const errMsg = payload?.msg || payload?.message || "Failed to create card product.";
        toast.error(errMsg, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    } catch (error) {
      console.error("Add card product request failed:", error);
      toast.error(error.message, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setModalSubmitting(false);
    }
  };

  const handlePageChange = useCallback(
    (newPageNo) => {
      fetchCardProducts({
        cardProductName: null,
        bin: null,
        productId: null,
        pageNo: Number(newPageNo),
        pageSize: pagination.pageSize,
      });
    },
    [fetchCardProducts, pagination.pageSize],
  );

  const handlePageSizeChange = useCallback(
    (newPageSize) => {
      fetchCardProducts({
        cardProductName: null,
        bin: null,
        productId: null,
        pageNo: 0,
        pageSize: newPageSize,
      });
    },
    [fetchCardProducts],
  );

  const handleFilter = useCallback(
    (filterPayload) => {
      fetchCardProducts(filterPayload);
    },
    [fetchCardProducts],
  );
  const handleToggleDefault = useCallback(
    async (item) => {
      if (!item?.id) return;
      setTableLoading(true);
      try {
        const res = await markCardProductDefaultRequest({ id: item.id });
        const payload = res?.data ?? res;
        if (payload?.code === "0000") {
          toast.success(payload?.message || "Marked as default", {
            position: "top-right",
            autoClose: 3000,
          });
          // refresh current page
          fetchCardProducts({
            cardProductName: null,
            bin: null,
            productId: null,
            // API pageNo is 0-based (Spring Page.number)
            pageNo: pagination.pageNo,
            pageSize: pagination.pageSize || 10,
          });
        } else {
          const msg = payload?.msg || payload?.message || "Failed to mark default";
          toast.error(msg, { position: "top-right", autoClose: 3000 });
        }
      } catch (err) {
        console.error("Toggle default failed:", err);
        toast.error(err?.message || "Network error", { position: "top-right", autoClose: 3000 });
      } finally {
        setTableLoading(false);
      }
    },
    [fetchCardProducts, pagination.pageNo, pagination.pageSize],
  );
  return (
    <>
      <div className="flex flex-center space-between mb10">
        <Heading size="xxl">Card Product Management</Heading>

        <ButtonOutline rg className="flex flex-center" primary onClick={handleAddCardProduct}>
          Add New Card Product
        </ButtonOutline>
      </div>
      <Box
        sx={{
          p: 3,
          maxWidth: 1200,
          margin: "auto",
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 3,
        }}
      >
        <Filter onFilter={handleFilter} />
        {tableLoading ? (
          <CircularProgress />
        ) : (
          <CardProductTable
            data={cardProducts}
            pagination={{
              // GenericMUITable + MUI Pagination use 1-based page; API stores 0-based pageNo
              currentPage: pagination.pageNo + 1,
              totalPages: pagination.totalPages,
              totalRecords: pagination.totalElements,
              itemsPerPage: pagination.pageSize,
              onPageChange: (page1Based) => handlePageChange(page1Based - 1),
              onItemsPerPageChange: (e) => {
                const newSize = parseInt(e.target.value, 10);
                handlePageSizeChange(newSize);
              },
            }}
            onToggleDefault={handleToggleDefault}
            loading={tableLoading}
          />
        )}
        <AddCardProductModal
          open={modalOpen}
          handleClose={handleModalClose}
          onSubmit={handleModalSubmit}
          isSubmitting={modalSubmitting}
        />
      </Box>
    </>
  );
};

export default CardProductManagement;
